import React from 'react';
import { Box, Typography, Button, Container, Paper, Fade } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { Block as BlockIcon, Home as HomeIcon, ContactSupport as ContactIcon } from '@mui/icons-material';

const AccessDenied = ({ 
  title = "Access Denied", 
  message = "You don't have permission to access this page.",
  moduleName = null,
  actionName = null,
  requiredPermission = null,
  showContactSupport = true 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get permission info from navigation state if available
  const stateModuleName = location.state?.moduleName || moduleName;
  const stateActionName = location.state?.actionName || actionName;
  const deniedPath = location.state?.deniedPath;

  // Determine home route based on current path (merchant vs admin)
  const handleGoHome = () => {
    const currentPath = window.location.pathname;
    if (currentPath.startsWith('/glam-admin')) {
      navigate('/glam-admin/dashboard');
    } else {
      navigate('/dashboard'); // Merchant dashboard
    }
  };

  // Generate specific permission message
  const getPermissionMessage = () => {
    if (stateModuleName && stateActionName) {
      return `You need "${stateActionName}" permission for "${stateModuleName}" module to access this page.`;
    }
    if (requiredPermission) {
      return `Required permission: ${requiredPermission}`;
    }
    return message;
  };

  const handleGoBack = () => {
    // If we know the denied path, avoid going back to it
    if (deniedPath && window.location.pathname !== deniedPath) {
      // We can safely go back since we're not at the denied path
      navigate(-1);
    } else {
      // Either we don't know the denied path or we're at it, so go to dashboard
      handleGoHome();
    }
  };

  return (
    <Fade in={true} timeout={300}>
      <Container maxWidth="md">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="100vh"
          textAlign="center"
        >
          <Paper elevation={3} sx={{ p: 6, borderRadius: 2 }}>
            <BlockIcon sx={{ fontSize: 120, color: 'warning.main', mb: 2 }} />
            
            <Typography variant="h3" component="h1" gutterBottom color="warning.main" sx={{ fontWeight: 'bold' }}>
              {title}
            </Typography>
            
            <Typography variant="h6" component="h2" gutterBottom color="text.secondary" sx={{ mb: 3 }}>
              {getPermissionMessage()}
            </Typography>
            
            {(stateModuleName || requiredPermission) && (
              <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', fontStyle: 'italic' }}>
                {stateModuleName && stateActionName && (
                  <>Module: <strong>{stateModuleName}</strong> | Action: <strong>{stateActionName}</strong></>
                )}
                {!stateModuleName && requiredPermission && (
                  <>Required permission: <strong>{requiredPermission}</strong></>
                )}
                {deniedPath && (
                  <><br />Attempted to access: <strong>{deniedPath}</strong></>
                )}
              </Typography>
            )}
            
            <Typography variant="body1" sx={{ mb: 4, maxWidth: 500, textAlign: 'center' }}>
              If you believe this is an error, please contact your administrator to request access to this resource.
            </Typography>
            
            <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
              <Button
                variant="contained"
                color="primary"
                startIcon={<HomeIcon />}
                onClick={handleGoHome}
                size="large"
              >
                Go to Dashboard
              </Button>
              
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ContactIcon />}
                onClick={() => navigate('/contact-us')}
                size="large"
              >
                Contact Support
              </Button>
              
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleGoBack}
                size="large"
              >
                Go Back
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    </Fade>
  );
};

export default AccessDenied; 